import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAttempts, useScenarios } from "../api/queries";
import { exportAttemptsCsv } from "../api/simPatient";
import type { AttemptListItem, AttemptStatus } from "../api/types";
import { AttemptModal } from "../components/SimPatient";
import { DotStatus, FilterButton } from "../components/sim/ui";
import { ATTEMPT_TONE, pct, score, STATUS_LABELS } from "../lib/sim";
import {
  BrandButton,
  CoursePageHeader,
  DataTable,
  EmptyState,
  HeadCell,
  NumberPager,
  RowCell,
  SelectInput,
} from "../components/courses/ui";
import { ErrorBanner, Loading, RelativeTime } from "../components/ui";
import { duration } from "../lib/format";

const PAGE_SIZE = 50;
const STATUSES: AttemptStatus[] = ["in_progress", "completed", "abandoned"];
const SINCE: { label: string; days?: number }[] = [
  { label: "Any time" },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

function sinceIso(days?: number) {
  if (!days) return undefined;
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

// One attempt row: learner, scenario, score, turns, timings, status. Shared by
// the attempts page and the scenario detail's Attempts tab.
function AttemptRow({ a, showScenario, onOpen }: {
  a: AttemptListItem;
  showScenario: boolean;
  onOpen: (id: number) => void;
}) {
  return (
    <tr className="cursor-pointer hover:bg-black/[0.02]" onClick={() => onOpen(a.id)}>
      <RowCell>
        <button
          className="text-left font-medium text-gray-900 hover:text-[#0B5785] hover:underline"
          onClick={(e) => { e.stopPropagation(); onOpen(a.id); }}
        >
          {a.user_full_name ?? a.user_email ?? `user ${a.user_id}`}
        </button>
        {a.user_full_name && <div className="mt-0.5 text-xs text-gray-500">{a.user_email}</div>}
      </RowCell>
      {showScenario && (
        <RowCell>
          <Link
            to={`/sim-patients/${a.scenario_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-900 hover:text-[#0B5785] hover:underline"
          >
            {a.scenario_title}
          </Link>
        </RowCell>
      )}
      <RowCell className="whitespace-nowrap tabular-nums">
        {score(a.total_score, a.max_possible_score)}
        {a.score_pct !== null && <span className="ml-1.5 text-xs font-normal text-gray-400">{pct(a.score_pct)}</span>}
      </RowCell>
      <RowCell className="tabular-nums">{a.turn_count}</RowCell>
      <RowCell><RelativeTime iso={a.started_at} /></RowCell>
      <RowCell className="whitespace-nowrap text-gray-600">{a.finished_at ? duration(a.started_at, a.finished_at) : "—"}</RowCell>
      <RowCell><DotStatus tone={ATTEMPT_TONE[a.status] ?? "gray"} label={STATUS_LABELS[a.status]} /></RowCell>
    </tr>
  );
}

function attemptHead(showScenario: boolean) {
  return (
    <>
      <HeadCell>Learner</HeadCell>
      {showScenario && <HeadCell>Scenario</HeadCell>}
      <HeadCell>Score</HeadCell>
      <HeadCell>Turns</HeadCell>
      <HeadCell>Started</HeadCell>
      <HeadCell>Duration</HeadCell>
      <HeadCell>Status</HeadCell>
    </>
  );
}

// ?scenario=<id>, ?status=<attempt status>, ?days=7|30|90
export function SimAttemptsPage() {
  const [params, setParams] = useSearchParams();
  const scenarioId = params.get("scenario") ? Number(params.get("scenario")) : undefined;
  const status = STATUSES.includes(params.get("status") as AttemptStatus) ? (params.get("status") as AttemptStatus) : undefined;
  const days = Number(params.get("days")) || undefined;
  const scenarios = useScenarios({ offset: 0, limit: 200 });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const [offset, setOffset] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const update = (next: Record<string, string | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
    setOffset(0);
  };

  const since = sinceIso(days);
  const attempts = useAttempts({ scenarioId, status, since, offset, limit: PAGE_SIZE });
  const rows = attempts.data?.attempts ?? [];
  const total = attempts.data?.total;
  const showScenario = !scenarioId;

  const download = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await exportAttemptsCsv({ scenarioId, status, since });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sim-patient-attempts.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="px-8 py-10">
      <CoursePageHeader
        title="Simulation attempts"
        subtitle="Every learner run through a simulated patient. Open one to read the conversation and see what was scored."
        actions={<BrandButton variant="primary" loading={exporting} onClick={download}>Export CSV</BrandButton>}
      />
      <ErrorBanner error={exportError} />

      <div className="mt-6">
        <h2 className="mb-4 text-[15px] font-semibold text-gray-900">Attempts</h2>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <div className="relative ml-auto">
            <FilterButton open={showFilters} onToggle={() => setShowFilters((v) => !v)} />
            {showFilters && (
              <div className="absolute right-0 z-20 mt-2 w-72 space-y-3 rounded-lg border border-gray-100 bg-white p-4 text-sm shadow-lg">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-500">Scenario</span>
                  <SelectInput aria-label="Scenario" value={scenarioId ?? ""} onChange={(e) => update({ scenario: e.target.value || undefined })}>
                    <option value="">All scenarios</option>
                    {(scenarios.data?.scenarios ?? []).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </SelectInput>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-500">Status</span>
                  <SelectInput aria-label="Status" value={status ?? ""} onChange={(e) => update({ status: e.target.value || undefined })}>
                    <option value="">Any status</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </SelectInput>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-500">Started</span>
                  <SelectInput aria-label="Started" value={days ?? ""} onChange={(e) => update({ days: e.target.value || undefined })}>
                    {SINCE.map((o) => <option key={o.label} value={o.days ?? ""}>{o.label}</option>)}
                  </SelectInput>
                </label>
              </div>
            )}
          </div>
        </div>

        <ErrorBanner error={attempts.error} />
        {attempts.isLoading ? <Loading /> : !rows.length ? <EmptyState>No attempts match.</EmptyState> : (
          <div className="overflow-hidden rounded-lg bg-white">
            <DataTable minWidth={1000} head={attemptHead(showScenario)}>
              {rows.map((a) => <AttemptRow key={a.id} a={a} showScenario={showScenario} onOpen={setOpenId} />)}
            </DataTable>
          </div>
        )}
        <NumberPager
          page={Math.floor(offset / PAGE_SIZE)}
          pages={total !== undefined ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : undefined}
          hasNext={total !== undefined ? offset + rows.length < total : rows.length === PAGE_SIZE}
          onChange={setOffset}
        />
      </div>

      {openId !== null && <AttemptModal attemptId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// Paged attempts table plus the detail modal. Reused on a scenario's Attempts tab.
export function SimAttemptsTable({ scenarioId, status, since, showScenario = false }: {
  scenarioId?: number;
  status?: AttemptStatus;
  since?: string;
  showScenario?: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const attempts = useAttempts({ scenarioId, status, since, offset, limit: PAGE_SIZE });
  const rows = attempts.data?.attempts ?? [];
  const total = attempts.data?.total;

  return (
    <>
      <ErrorBanner error={attempts.error} />
      {attempts.isLoading ? <Loading /> : !rows.length ? <EmptyState>No attempts match.</EmptyState> : (
        <DataTable minWidth={900} head={attemptHead(showScenario)}>
          {rows.map((a) => <AttemptRow key={a.id} a={a} showScenario={showScenario} onOpen={setOpenId} />)}
        </DataTable>
      )}
      <NumberPager
        page={Math.floor(offset / PAGE_SIZE)}
        pages={total !== undefined ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : undefined}
        hasNext={total !== undefined ? offset + rows.length < total : rows.length === PAGE_SIZE}
        onChange={setOffset}
      />
      {openId !== null && <AttemptModal attemptId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
